import {
  batchTrackProvinceEvents,
  getProvinceAnalyticsData,
  initProvinceAnalytics,
  trackProvinceConversion,
  trackProvinceInteraction,
  trackProvincePageView,
  trackProvinceTemplatePerformance,
  trackProvinceTemplateSelection
} from "@/lib/argentinaAnalytics";
import { useCallback, useEffect, useRef } from "react";
import { useArgentinaTemplate } from "./useArgentinaTemplate";

/**
 * Hook for Argentina Province Analytics
 * Provides easy-to-use analytics functions with automatic province context
 */
export const useProvinceAnalytics = () => {
  const { provinceCode, templateName, provinceConfig } = useArgentinaTemplate();
  const performanceRef = useRef({
    pageLoadStart: Date.now(),
    interactions: 0,
    ctaClicks: 0,
    maxScrollDepth: 0
  });

  // Initialize analytics on mount
  useEffect(() => {
    if (provinceCode && provinceCode !== "AR_UNKNOWN") {
      initProvinceAnalytics();
      
      // Track template selection
      if (templateName) {
        trackProvinceTemplateSelection(templateName);
      }
    }
  }, [provinceCode, templateName]);

  // Track page changes
  useEffect(() => {
    const handlePageChange = () => {
      if (provinceCode) {
        trackProvincePageView(window.location.pathname, document.title);
      }
    };

    // Listen for route changes (for SPAs)
    window.addEventListener('popstate', handlePageChange);
    return () => window.removeEventListener('popstate', handlePageChange);
  }, [provinceCode]);

  // Track scroll depth
  useEffect(() => {
    const handleScroll = () => {
      const scrollPercent = Math.round(
        (window.scrollY / (document.documentElement.scrollHeight - window.innerHeight)) * 100
      );
      
      if (scrollPercent > performanceRef.current.maxScrollDepth) {
        performanceRef.current.maxScrollDepth = scrollPercent;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Track performance metrics when component unmounts or page changes
  useEffect(() => {
    return () => {
      if (provinceCode && templateName) {
        const currentPerf = performanceRef.current;
        const loadTime = Date.now() - currentPerf.pageLoadStart;
        trackProvinceTemplatePerformance({
          templateName,
          loadTime,
          scrollDepth: currentPerf.maxScrollDepth,
          ctaClicks: currentPerf.ctaClicks
        });
      }
    };
  }, [provinceCode, templateName]);

  // Analytics functions with automatic province context
  const trackInteraction = useCallback((interactionType: string, details: Record<string, unknown> = {}) => {
    performanceRef.current.interactions++;
    trackProvinceInteraction(interactionType, {
      ...details,
      total_interactions: performanceRef.current.interactions
    });
  }, []);

  const trackConversion = useCallback((conversionType: string, value?: number, currency?: string) => {
    trackProvinceConversion(conversionType, value, currency);
  }, []);

  const trackCTAClick = useCallback((ctaName: string, ctaLocation: string) => {
    performanceRef.current.ctaClicks++;
    trackInteraction('cta_click', {
      cta_name: ctaName,
      cta_location: ctaLocation,
      total_cta_clicks: performanceRef.current.ctaClicks
    });
  }, [trackInteraction]);

  const trackFormSubmission = useCallback((formName: string, formData?: Record<string, unknown>) => {
    trackInteraction('form_submission', {
      form_name: formName,
      form_data: formData
    });
  }, [trackInteraction]);

  const trackSocialMediaClick = useCallback((platform: string, action: string) => {
    trackInteraction('social_media_click', {
      platform,
      action
    });
  }, [trackInteraction]);

  const trackVideoInteraction = useCallback((videoId: string, action: 'play' | 'pause' | 'complete', duration?: number) => {
    trackInteraction('video_interaction', {
      video_id: videoId,
      action,
      duration_seconds: duration
    });
  }, [trackInteraction]);

  const trackImageView = useCallback((imageId: string, imageType: string) => {
    trackInteraction('image_view', {
      image_id: imageId,
      image_type: imageType
    });
  }, [trackInteraction]);

  const trackSearchQuery = useCallback((query: string, resultsCount?: number) => {
    trackInteraction('search_query', {
      query,
      results_count: resultsCount
    });
  }, [trackInteraction]);

  const trackModalOpen = useCallback((modalName: string, trigger: string) => {
    trackInteraction('modal_open', {
      modal_name: modalName,
      trigger
    });
  }, [trackInteraction]);

  const trackDownload = useCallback((fileName: string, fileType: string, fileSize?: number) => {
    trackInteraction('file_download', {
      file_name: fileName,
      file_type: fileType,
      file_size_bytes: fileSize
    });
  }, [trackInteraction]);

  // Batch tracking for performance
  const batchTrackEvents = useCallback((events: Array<{
    type: 'interaction' | 'conversion' | 'performance';
    data: Record<string, unknown>;
  }>) => {
    batchTrackProvinceEvents(events);
  }, []);

  // Get current analytics data
  const getAnalyticsData = useCallback(() => {
    return getProvinceAnalyticsData();
  }, []);

  // Enhanced province information
  const provinceAnalytics = {
    isTracking: !!provinceCode && provinceCode !== "AR_UNKNOWN",
    provinceCode,
    provinceName: provinceConfig?.provinceName,
    provinceRegion: provinceConfig?.region,
    economicLevel: provinceConfig?.economicLevel,
    templateName,
    sessionMetrics: {
      interactions: performanceRef.current.interactions,
      ctaClicks: performanceRef.current.ctaClicks,
      maxScrollDepth: performanceRef.current.maxScrollDepth,
      sessionDuration: Date.now() - performanceRef.current.pageLoadStart
    }
  };

  return {
    // Province information
    provinceAnalytics,
    
    // Basic tracking functions
    trackInteraction,
    trackConversion,
    
    // Specific interaction tracking
    trackCTAClick,
    trackFormSubmission,
    trackSocialMediaClick,
    trackVideoInteraction,
    trackImageView,
    trackSearchQuery,
    trackModalOpen,
    trackDownload,
    
    // Advanced functions
    batchTrackEvents,
    getAnalyticsData,
    
    // Utility functions
    isTracking: provinceAnalytics.isTracking,
  };
};